import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { SERVICES, USE_CASES } from "@/data/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const related = USE_CASES.filter((u) =>
    u.tags.some((t) => t.toLowerCase().includes(service.name.split(" ")[0].toLowerCase())),
  ).slice(0, 2);

  return (
    <div data-testid={`service-detail-${slug}`} className="bg-white">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-slate-200 overflow-hidden bg-slate-50">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${service.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-600 hover:text-[#1D4ED8]"
            data-testid="back-to-services"
          >
            <ArrowLeft size={14} weight="bold" /> All services
          </Link>
          <div className="mt-8 grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline mb-5">Service</div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-black text-slate-900">
                {service.name}
              </h1>
            </div>
            <div className="md:col-span-4">
              <p className="text-base md:text-lg text-slate-700 leading-relaxed font-light">
                {service.short}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 md:py-32 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="overline mb-5">Overview</div>
            <p className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight leading-snug">
              {service.description}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="card-flat p-7 md:p-8">
              <div className="overline mb-5">Tech & standards</div>
              <div className="flex flex-wrap gap-2">
                {service.stack.map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1.5 border border-slate-300 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-700"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-20 md:py-32 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-5">What we deliver</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight max-w-3xl">
            Concrete outcomes — not slideware.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {service.deliverables.map((d, i) => (
              <div
                key={d}
                data-testid={`deliverable-${slug}-${i}`}
                className="card-flat p-6 flex gap-4 items-start"
              >
                <CheckCircle size={20} weight="bold" className="text-[#1D4ED8] shrink-0 mt-0.5" />
                <span className="text-sm text-slate-800 leading-relaxed">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related cases */}
      {related.length > 0 && (
        <section className="py-20 md:py-32 bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="overline mb-5">Selected work</div>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                  In production with this service.
                </h2>
              </div>
              <Link to="/use-cases" className="btn-ghost" data-testid={`detail-${slug}-cases`}>
                All cases
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {related.map((c, i) => (
                <article key={i} className="card-flat p-7 md:p-9">
                  <div className="overline">{c.tags.join(" · ")}</div>
                  <h3 className="mt-4 font-display text-xl md:text-2xl font-semibold text-slate-900">{c.client}</h3>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{c.sector}</div>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">{c.solution}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA + Next */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-6 items-stretch">
          <Link
            to="/contact"
            data-testid={`detail-${slug}-cta`}
            className="md:col-span-8 bg-[#1D4ED8] hover:bg-[#1E3A8A] transition-colors p-10 md:p-14 flex flex-col justify-between text-white"
          >
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/85 font-bold">Engage with us</div>
            <h3 className="mt-6 font-display text-3xl md:text-5xl font-black tracking-tighter leading-[1.05]">
              Get a proposal for {service.name}.
            </h3>
            <div className="mt-10 inline-flex items-center gap-2 font-mono text-xs tracking-[0.22em] uppercase">
              Start a project <ArrowUpRight size={16} weight="bold" />
            </div>
          </Link>

          <Link
            to={`/services/${next.slug}`}
            data-testid={`detail-${slug}-next`}
            className="md:col-span-4 bg-white border border-slate-300 hover:border-[#1D4ED8] hover:bg-slate-50 transition-colors p-10 md:p-14 flex flex-col justify-between"
          >
            <div className="overline">Up next</div>
            <div>
              <div className="mt-6 font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
                {next.name}
              </div>
              <div className="mt-3 inline-flex items-center gap-2 font-mono text-xs tracking-[0.22em] uppercase text-slate-600">
                Read service <ArrowUpRight size={14} weight="bold" />
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
